import React from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Button } from '../ui/button';
import { faqData, faqMobileData } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getGenericWhatsAppLink } from '../../utils/whatsapp';

// WhatsApp SVG Icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// English FAQ data (SINGULAR form - I/you)
const faqDataEn = [
  {
    question: "How do I book an excursion or a pack?",
    answer: "Booking is done directly on the site in just a few seconds. You choose your excursion or pack, pay a secure deposit, and I will contact you immediately on WhatsApp to confirm all the details."
  },
  {
    question: "Is online payment secure?",
    answer: "Yes. All online payments are 100% secure via Stripe, one of the most reliable payment platforms in the world. Your bank details are never stored on my site."
  },
  {
    question: "Do I have to pay the full amount in advance?",
    answer: "No. You only pay a deposit at the time of booking. The balance is to be paid on site on the day of the excursion, with no hidden fees."
  },
  {
    question: "What happens after paying the deposit?",
    answer: "Once the deposit is paid, I contact you directly on WhatsApp to confirm availability, exact time, and organize pickup from your hotel. You always have a human contact."
  },
  {
    question: "Can I ask questions before booking?",
    answer: "Yes. You can contact me anytime on WhatsApp, before or after booking. I respond quickly and clearly to all your questions."
  },
  {
    question: "What happens in case of cancellation?",
    answer: "In case of cancellation with sufficient notice, I always find a solution: date change or cancellation depending on the situation. Your peace of mind is my priority."
  },
  {
    question: "Are hotel transfers included?",
    answer: "Yes. Pickup from your hotel in Hurghada is included for all excursions and packs, unless otherwise stated on the activity page."
  },
  {
    question: "Are excursions suitable for children?",
    answer: "Yes, most of my excursions are suitable for families and children. If an activity requires a minimum age or specific conditions, it is always specified."
  },
  {
    question: "Will I be accompanied by an English-speaking guide?",
    answer: "For some cultural excursions (Luxor, Cairo), a guide is included. For other activities, I remain available via WhatsApp before and during the excursion."
  }
];

const faqMobileDataEn = [
  {
    question: "Is the payment secure?",
    answer: "Yes, the deposit payment is secure via Stripe."
  },
  {
    question: "Do I have to pay everything upfront?",
    answer: "No, only a deposit. The balance is paid on site."
  },
  {
    question: "Will I be contacted after booking?",
    answer: "Yes, I contact you immediately on WhatsApp after payment."
  },
  {
    question: "Is hotel transfer included?",
    answer: "Yes, unless otherwise stated on the activity page."
  },
  {
    question: "Can I ask questions before?",
    answer: "Yes, you can write to me anytime on WhatsApp."
  }
];

const FAQSection = () => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const whatsappLink = getGenericWhatsAppLink(language);
  
  const isEnglish = language === 'en';
  const currentFaqData = isEnglish ? faqDataEn : faqData;
  const currentFaqMobileData = isEnglish ? faqMobileDataEn : faqMobileData;

  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-slate-100 text-slate-600 rounded-full text-sm font-medium mb-4">
            {t('faq.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            {t('faq.title')}
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        {/* FAQ Mobile - Version courte (visible uniquement sur mobile) */}
        <div className="md:hidden">
          <Accordion type="single" collapsible className="space-y-3">
            {currentFaqMobileData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`mobile-item-${index}`}
                className="bg-slate-50 rounded-xl border border-slate-100 px-5 hover:border-teal-200 transition-colors data-[state=open]:border-teal-300 data-[state=open]:bg-teal-50/50"
              >
                <AccordionTrigger className="text-left text-slate-800 font-semibold py-4 hover:no-underline hover:text-teal-600 transition-colors [&[data-state=open]]:text-teal-600">
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                    <span className="text-sm">{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-4 pl-8 leading-relaxed text-sm">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* FAQ Desktop - Version complète (visible uniquement sur desktop) */}
        <div className="hidden md:block">
          <Accordion type="single" collapsible className="space-y-4">
            {currentFaqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl border border-slate-100 px-6 hover:border-teal-200 transition-colors data-[state=open]:border-teal-300 data-[state=open]:bg-teal-50/50"
              >
                <AccordionTrigger className="text-left text-slate-800 font-semibold py-5 hover:no-underline hover:text-teal-600 transition-colors [&[data-state=open]]:text-teal-600">
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 pl-8 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Soft WhatsApp reminder */}
        <div className="mt-12 text-center p-6 bg-slate-50 rounded-2xl border border-slate-100">
          <p className="text-slate-600 mb-4">
            {t('faq.moreQuestions')}
          </p>
          <Button
            asChild
            variant="outline"
            className="rounded-full border-2 border-teal-500 text-teal-600 hover:bg-teal-500 hover:text-white transition-all duration-300"
          >
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon className="w-4 h-4 mr-2" />
              {t('faq.askQuestion')}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
